import { ComponentProps } from "react";
import { BlockTitle } from "@blocks/Blocks";
import { useSetPageParam } from "@hooks/useSetPageParam";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { FeedTag } from "@routes/Keyword/utils/types";
import { cn } from "@util/helpers";

type PropertySupplementsGroupProps = {
  /** The tag for the group */
  tag: FeedTag;
  /** The articles props for the group */
  articlesProps: ComponentProps<typeof Card>[];
  /** The index for the group */
  index?: number;
  /** The className for the group */
  className?: string;
};

const SupplementsGroup = ({
  tag,
  articlesProps,
  index = 0,
  className,
}: PropertySupplementsGroupProps): React.ReactElement => {
  const { ref } = useSetPageParam({ cardIndex: index });

  return (
    <div
      data-testid="wealth-supplements-group-component"
      className={className}
      ref={ref}
    >
      <BlockTitle
        link={tag.urlPath}
        text={tag.name.toUpperCase()}
        rootClassName="mb-2"
      />

      <div
        className={cn("grid flex-grow grid-cols-1", {
          "grid grid-cols-1 lg:grid-cols-2": index === 0,
        })}
      >
        {articlesProps.map((cardProps, _index) => {
          const isHighlightArticle = index === 0 && _index === 0;
          const isHighlightExtra = index === 0 && _index !== 0;

          const variant: CardVariations = {
            image: {
              position: "left",
              width: cn("w-2/5", { "w-full lg:w-2/5": isHighlightArticle }),
            },
            content: {
              width: cn("w-3/5", { "w-full lg:w-3/5": isHighlightArticle }),
              extraClass: cn({
                "pl-4": cardProps.thumbnail,
                "pl-0 pt-4 lg:pl-4 lg:pt-0": isHighlightArticle,
                "pl-0": isHighlightExtra,
              }),
              blurb: { wrapperClassName: "mt-1" },
            },
            title: {
              size: isHighlightArticle ? "text-lg lg:text-6xl" : "text-lg",
              color: "text-gray-850",
              extraClass: cn("font-bold font-lct"),
            },
            kicker: {
              color: "text-gray-515",
            },
          };

          return (
            <Card
              key={cardProps.id}
              {...cardProps}
              variations={variant}
              className={cn("bg-gray-150 p-4", {
                "h-full": index !== 0,
                "col-span-1 border-b border-gray-175 lg:col-span-2":
                  isHighlightArticle,
                "border-b border-gray-175 lg:border-none":
                  index === 0 && _index === 1,
              })}
            />
          );
        })}
      </div>
    </div>
  );
};

export default SupplementsGroup;
